import { useState } from 'react'
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import DashboardIcon from '@mui/icons-material/GridViewRounded'
import CalculateIcon from '@mui/icons-material/CalculateRounded'
import FolderIcon from '@mui/icons-material/FolderRounded'
import LogoutIcon from '@mui/icons-material/LogoutRounded'
import CottageIcon from '@mui/icons-material/CottageRounded'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsRounded'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { palette } from '../theme'

const drawerWidth = 244

const navItems = [
  { to: '/', label: 'Tableau de bord', icon: <DashboardIcon />, end: true },
  { to: '/simulations', label: 'Simulations', icon: <CalculateIcon />, end: false },
  { to: '/projects', label: 'Projets', icon: <FolderIcon />, end: false },
]

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const items =
    user?.role === 'admin'
      ? [...navItems, { to: '/admin', label: 'Administration', icon: <AdminIcon />, end: false }]
      : navItems

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase()

  const sidebar = (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: palette.sidebar }}
    >
      {/* Spacer so the fixed mobile app bar doesn't cover the first item */}
      <Toolbar sx={{ display: { md: 'none' } }} />
      <List sx={{ px: 1.5, pt: 3, flexGrow: 1 }}>
        {items.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.end}
            onClick={() => setMobileOpen(false)}
            disableRipple
            sx={{
              borderRadius: 2,
              mb: 0.75,
              py: 1,
              color: 'text.secondary',
              '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 38 },
              '&:hover': { bgcolor: 'rgba(245,245,245,0.05)', color: 'text.primary' },
              '&.active': {
                bgcolor: palette.sidebarActive,
                color: 'text.primary',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
              },
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 600 } } }}
            />
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ px: 2, pb: 1, textAlign: 'center' }}>
        <CottageIcon sx={{ fontSize: 30, color: 'text.primary' }} />
        <Typography sx={{ fontWeight: 800, letterSpacing: 0.4, mt: 0.5 }}>Immo Gérer</Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(245,245,245,0.10)', mx: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 2 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: palette.card, fontSize: 13 }}>
          {initials}
        </Avatar>
        <Typography
          variant="body2"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {user?.email}
        </Typography>
        <Tooltip title="Déconnexion">
          <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.background }}>
      {/* Mobile top bar */}
      <AppBar position="fixed" sx={{ display: { md: 'none' }, zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Immo Gérer
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: drawerWidth },
        }}
      >
        {sidebar}
      </Drawer>

      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            bgcolor: palette.background,
            p: { xs: 2, md: 4 },
            mt: { xs: 7, md: 0 },
            overflowX: 'hidden',
          }}
        >
          <Outlet />
        </Box>

        <Box
          component="nav"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            borderLeft: '1px solid rgba(245,245,245,0.08)',
          }}
        >
          {sidebar}
        </Box>
      </Box>
    </Box>
  )
}
